import { useParams, Navigate, Link } from "react-router-dom";
import InfoLayout from "@/components/InfoLayout";
import InlineCTA from "@/components/InlineCTA";
import { CITIES, COMPANY, SERVICES, FAQS, SITE_URL } from "@/seo/data";

export default function StadtPage() {
  const { citySlug } = useParams();
  const city = CITIES.find((c) => c.slug === citySlug);
  if (!city) return <Navigate to="/leistungen" replace />;

  const title = `Autoglas ${city.name} | Steinschlag & Scheibentausch in 24 h`;
  const description = `Autoglas-Service in ${city.name}: Steinschlagreparatur, Frontscheibentausch und ADAS-Kalibrierung mit Direktabwicklung Ihrer Versicherung. Termin in 24 h.`;
  const canonical = `${SITE_URL}/standort/${city.slug}`;

  const localFaqs = FAQS.slice(0, 8);
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AutoRepair",
        name: `${COMPANY.name} – ${city.name}`,
        image: canonical,
        url: canonical,
        telephone: COMPANY.phone,
        priceRange: "€€",
        address: {
          "@type": "PostalAddress",
          streetAddress: COMPANY.address_street,
          addressLocality: COMPANY.address_city,
          addressCountry: COMPANY.address_country,
        },
        areaServed: { "@type": "City", name: city.name },
        aggregateRating: { "@type": "AggregateRating", ratingValue: String(COMPANY.rating), reviewCount: String(COMPANY.rating_count) },
      },
      {
        "@type": "FAQPage",
        mainEntity: localFaqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <InfoLayout title={title} description={description} canonical={canonical} jsonLd={jsonLd}>
      <h1>Autoglas {city.name} — Steinschlag, Scheibentausch & Versicherung in einer Hand</h1>
      <p className="lead">
        Sie haben einen Steinschlag oder Glasschaden in {city.name} ({city.plz})? Wir kümmern uns um alles —
        von der Schadensmeldung bei Ihrer Versicherung bis zur professionellen Montage einer neuen Scheibe in
        Erstausrüsterqualität. {city.serviceArea
          ? "Schaden in 5 Minuten online melden, Direktabwicklung mit allen großen Versicherern — Ihren Termin stimmen wir individuell mit Ihnen ab."
          : "Termin innerhalb 24 Stunden, Direktabwicklung mit allen großen Versicherern und Express-Service für die gesamte Metropolregion Rhein-Neckar."}
      </p>

      <h2>Unser Autoglas-Service für {city.name} und Umgebung</h2>
      <p>
        {city.serviceArea
          ? `Als spezialisierter Autoglas-Meisterbetrieb bedienen wir ${city.name} über unseren digitalen Schadenassistenten — Schadenmeldung, Versicherungsabwicklung und Terminkoordination laufen komplett online, den Termin stimmen wir individuell mit Ihnen ab.`
          : city.distance_km === 0
          ? `Unsere Werkstatt liegt direkt in ${city.name} — Frankenthaler Str. 98, 67059 Ludwigshafen. Für Sie bedeutet das: keine Anfahrt in eine andere Stadt, kurze Wege und schnelle Termine.`
          : `Als spezialisierter Autoglas-Betrieb mit Sitz in Ludwigshafen (${COMPANY.region}-Region) bedienen wir ${city.name} (Entfernung ca. ${city.distance_km} km zu unserer Werkstatt) mit kurzen Wegen und schnellen Terminen.`}
        {" "}Ob Frontscheibe, Seitenscheibe, Heckscheibe oder Panorama-Glasdach —
        unser geschultes Team arbeitet nach Herstellervorgabe und verwendet ausschließlich Scheiben in
        OE/OEM-Qualität. Auch komplexe Fahrzeuge mit Regensensor, Kamera-basierten Assistenzsystemen,
        Akustikverglasung oder beheizbarer Frontscheibe sind unser tägliches Geschäft.
      </p>

      <h2>Leistungen rund um Autoglas in {city.name}</h2>
      <ul>
        {SERVICES.map((s) => (
          <li key={s.slug}>
            <Link to={`/leistung/${s.slug}`}><strong>{s.name}</strong></Link> — {s.desc} (Dauer: {s.duration})
          </li>
        ))}
      </ul>

      <h2>Ablauf — so einfach geht's</h2>
      <ol>
        <li><strong>Schaden online melden:</strong> Sie laden Fotos vom Schaden, Fahrzeugschein und Versicherungsschein hoch. Unsere KI liest die Daten automatisch aus.</li>
        <li><strong>Termin wählen:</strong> Sie wählen einen freien Slot — meist schon am nächsten Werktag in {city.name} oder Umgebung.</li>
        <li><strong>Direktabwicklung mit Versicherung:</strong> Wir holen die Schadennummer ein und übernehmen die komplette Kommunikation mit HUK, Allianz, AXA und allen weiteren großen Versicherern.</li>
        <li><strong>Reparatur oder Tausch:</strong> Steinschlagreparatur ca. 30 Minuten, Frontscheibentausch 3–4 Stunden inkl. Aushärtung und ADAS-Kalibrierung.</li>
        <li><strong>Abholung:</strong> Sie holen Ihr Fahrzeug ab — oder wir bringen es zurück (auf Anfrage in {city.name} und Umgebung).</li>
      </ol>

      <h2>Kosten für Glasschaden in {city.name}</h2>
      <p>
        Steinschlagreparatur ist bei Teilkasko fast immer kostenlos — Versicherer verzichten in der Regel auf
        die Selbstbeteiligung, weil Reparatur deutlich günstiger ist als ein Komplettaustausch. Beim
        Frontscheibentausch fällt nur Ihre vertraglich vereinbarte Selbstbeteiligung an (typisch 150 €). Für
        Selbstzahler ohne Versicherung berechnen wir nach Upload des Fahrzeugscheins eine KI-gestützte
        Preisspanne von ca. 350–1.300 € inkl. Montage, abhängig von Ausstattung und Sensorik Ihres Fahrzeugs.
      </p>

      <h2>Versicherungen, mit denen wir in {city.name} direkt abrechnen</h2>
      <p>
        HUK-Coburg, Allianz, AXA, DEVK, LVM, ERGO, R+V, VHV, Signal Iduna, Generali, Gothaer, Provinzial, WGV
        und Württembergische — wir kennen die Anforderungen jedes Versicherers und reichen alle Unterlagen
        elektronisch ein. Mehr dazu auf unserer{" "}
        <Link to="/versicherungen">Versicherungsübersicht</Link>.
      </p>

      <InlineCTA />

      {city.serviceArea ? (
        <>
          <h2>So funktioniert unser Service für {city.name}</h2>
          <p>
            Sie melden Ihren Glasschaden bequem online — von überall in {city.name}. Wir prüfen die Unterlagen,
            übernehmen die komplette Versicherungsabwicklung und melden uns binnen 24 Stunden zur individuellen
            Terminabstimmung. Unsere Hauptwerkstatt befindet sich in {COMPANY.address_city}, {COMPANY.address_street}.
            Fragen Sie uns einfach per WhatsApp oder Telefon nach den aktuellen Möglichkeiten für {city.name}.
          </p>
        </>
      ) : (
        <>
          <h2>Anfahrt aus {city.name}</h2>
          <p>
            Unsere Hauptwerkstatt befindet sich in {COMPANY.address_city}, {COMPANY.address_street}. Aus
            {city.name} sind wir über die A6, A61 oder B9 in wenigen Minuten erreichbar. Auf Wunsch bieten wir
            Hol- und Bringservice in der gesamten Metropolregion Rhein-Neckar an — fragen Sie einfach per
            WhatsApp oder Telefon nach.
          </p>
        </>
      )}

      <h2>Häufige Fragen aus {city.name}</h2>
      {localFaqs.map((f, i) => (
        <div key={i}>
          <h3>{f.q}</h3>
          <p>{f.a}</p>
        </div>
      ))}

      <h2>Weitere Städte in der Region</h2>
      <p className="text-sm">
        {CITIES.filter((c) => c.slug !== city.slug).map((c, i, arr) => (
          <span key={c.slug}>
            <Link to={`/standort/${c.slug}`}>Autoglas {c.name}</Link>{i < arr.length - 1 ? " · " : ""}
          </span>
        ))}
      </p>
    </InfoLayout>
  );
}
